import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/renderbus-modal";
import { isDev } from "project-config/utils/env";
import { loadPaypalSdk } from "project-config/services/paypal-sdk";
import DebitCardCloseIcon from "./debit-card-close-icon";
import { DebitCardModalContent, DebitCardModalStyle } from "./atom";

type PaypalCreateOrderResult = {
  payRecordId: string;
  paypalOrderId: string;
};

type PaypalCaptureResult = {
  payRecordId?: string;
  paypalOrderId?: string;
  transactionId?: string;
  status?: string;
};

export function DebitCardModal({
  open,
  rechargeAmount,
  paypalCurrency,
  paypalBuyerCountry,
  createOrder,
  captureOrder,
  onPaymentSuccess,
  onClose,
}: {
  open: boolean;
  rechargeAmount: string;
  paypalCurrency: string;
  paypalBuyerCountry?: string;
  createOrder: () => Promise<PaypalCreateOrderResult>;
  captureOrder: (
    payload: PaypalCreateOrderResult
  ) => Promise<PaypalCaptureResult>;
  onPaymentSuccess?: (result: PaypalCaptureResult) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation("topup");
  const paypalButtonsContainerRef = useRef<HTMLDivElement>(null);
  const latestOrderRef = useRef<PaypalCreateOrderResult | null>(null);
  const createOrderRef = useRef(createOrder);
  const captureOrderRef = useRef(captureOrder);
  const onPaymentSuccessRef = useRef(onPaymentSuccess);
  const onCloseRef = useRef(onClose);
  const [isSdkLoading, setIsSdkLoading] = useState(false);
  const paypalClientId =
    (isDev()
      ? process.env.REACT_APP_PAYPAL_CLIENT_ID_TEST
      : process.env.REACT_APP_PAYPAL_CLIENT_ID_PROD) || "";
  const normalizedPaypalCurrency = (paypalCurrency || "USD").toUpperCase();
  const normalizedPaypalBuyerCountry = (paypalBuyerCountry || "").toUpperCase();

  useEffect(() => {
    createOrderRef.current = createOrder;
    captureOrderRef.current = captureOrder;
    onPaymentSuccessRef.current = onPaymentSuccess;
    onCloseRef.current = onClose;
  }, [captureOrder, createOrder, onClose, onPaymentSuccess]);

  useEffect(() => {
    if (!open) {
      latestOrderRef.current = null;
      setIsSdkLoading(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (!paypalClientId) {
      return;
    }

    let cancelled = false;
    let renderedContainer: HTMLDivElement | null = null;

    const waitForContainer = async () => {
      for (let attempt = 0; attempt < 20; attempt += 1) {
        const currentContainer = paypalButtonsContainerRef.current;
        if (currentContainer) {
          return currentContainer;
        }

        await new Promise<void>((resolve) => {
          window.setTimeout(resolve, 50);
        });

        if (cancelled) {
          return null;
        }
      }

      return null;
    };

    const finishLoading = () => {
      if (!cancelled) {
        setIsSdkLoading(false);
      }
    };

    const renderPaypalCardButton = async () => {
      setIsSdkLoading(true);
      try {
        const paypal = await loadPaypalSdk({
          clientId: paypalClientId,
          currency: normalizedPaypalCurrency,
          buyerCountry: normalizedPaypalBuyerCountry || undefined,
          components: ["buttons", "funding-eligibility"],
          enableFunding: ["card"],
        });

        const container = await waitForContainer();
        if (cancelled || !container) {
          if (!cancelled) {
            finishLoading();
          }
          return;
        }

        renderedContainer = container;
        container.innerHTML = "";

        const cardButton = paypal.Buttons({
          fundingSource: paypal.FUNDING.CARD,
          expandCardForm: true,
          style: {
            layout: "vertical",
            shape: "rect",
            label: "pay",
            color: "white",
          },
          createOrder: async () => {
            const order = await createOrderRef.current();
            latestOrderRef.current = order;
            return order.paypalOrderId;
          },
          onApprove: async () => {
            const latestOrder = latestOrderRef.current;
            if (!latestOrder) {
              throw new Error("PayPal order is missing.");
            }

            try {
              const captureResult = await captureOrderRef.current(latestOrder);
              onPaymentSuccessRef.current?.(captureResult);
              onCloseRef.current();
            } finally {
              latestOrderRef.current = null;
            }
          },
          onCancel: () => undefined,
          onError: () => undefined,
        });

        const isEligible = cardButton.isEligible();
        if (!isEligible) {
          finishLoading();
          return;
        }

        await cardButton.render(container);
        finishLoading();
      } catch (_error) {
        finishLoading();
      }
    };

    void renderPaypalCardButton();

    return () => {
      cancelled = true;
      latestOrderRef.current = null;
      if (renderedContainer) {
        renderedContainer.innerHTML = "";
      }
    };
  }, [
    open,
    paypalClientId,
    normalizedPaypalBuyerCountry,
    normalizedPaypalCurrency,
  ]);

  return (
    <>
      <DebitCardModalStyle />
      <Modal
        className="debit-card-modal"
        open={open}
        footer={null}
        closable={false}
        onCancel={onClose}
        width={335}
        centered
        destroyOnClose
      >
        <DebitCardModalContent>
          <div className="modal-header modal-header-paypal">
            <div
              className={`modal-header-main paypal-bcdc-container-wrapper ${
                isSdkLoading ? "sdk-loading" : ""
              }`}
            >
              <div
                className="paypal-bcdc-container"
                ref={paypalButtonsContainerRef}
              />
              {isSdkLoading ? (
                <div className="paypal-bcdc-loading" aria-hidden="true" />
              ) : null}
            </div>
            <button
              type="button"
              className="close-button"
              aria-label={t("fox_debit_card_close_aria")}
              onClick={onClose}
            >
              <DebitCardCloseIcon />
            </button>
          </div>
        </DebitCardModalContent>
      </Modal>
    </>
  );
}
